import React, { useState } from 'react';
import { Card, Form, Input, Button, Alert, Space, Typography, Divider, Tag } from 'antd';
import { useSensitiveCheck } from '../../../hooks/useSensitiveCheck';
import { initializeSensitiveWordsFromConfig } from '../../../utils/sensitiveWordsLoader';
import { useTranslation } from 'react-i18next';
import CentralLayout from '../../../layouts/CentralLayout';

const { TextArea } = Input;
const { Title, Paragraph, Text } = Typography;

const SensitiveWordsTestPage: React.FC = () => {
  const { t } = useTranslation();
  const [initialized, setInitialized] = useState(false);
  const [initializing, setInitializing] = useState(false);

  // 初始化敏感词列表
  const handleInitialize = async () => {
    setInitializing(true);
    try {
      const count = await initializeSensitiveWordsFromConfig();
      setInitialized(true);
      console.log(`已加载 ${count} 个敏感词`);
    } catch (error) {
      console.error('初始化失败:', error);
    } finally {
      setInitializing(false);
    }
  };

  // 使用敏感词检查Hook
  const {
    text,
    errorWords,
    errorMessage,
    isPass,
    handleChange,
    checkText,
  } = useSensitiveCheck({
    initialText: '',
    loadFromConfig: initialized,
    realtimeCheck: true,
    maxResults: 3,
    errorMessageTemplate: (words) => {
      // 多语言错误提示模板
      return t('sensitiveWords.contains', { words: words.join('、') }) || 
             `包含敏感词汇：${words.join('、')}，请修改！`;
    },
  });

  // 手动测试按钮
  const handleManualCheck = () => {
    const pass = checkText(text);
    if (!pass) {
      console.log('检测到的敏感词:', errorWords);
      console.log('错误提示:', errorMessage);
    }
  };

  return (
      <div className="p-6 max-w-4xl mx-auto">
        <Card>
          <Title level={2}>敏感词校验功能测试</Title>
          <Paragraph>
            这是一个敏感词校验功能的测试页面，用于演示前端敏感词检测功能。
          </Paragraph>

          <Divider orientation="left">初始化</Divider>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Button
              type="primary"
              onClick={handleInitialize}
              loading={initializing}
              disabled={initialized}
            >
              {initialized ? '已初始化' : '初始化敏感词列表'}
            </Button>
            {initialized && (
              <Alert
                message="敏感词列表已初始化"
                type="success"
                showIcon
              />
            )}
          </Space>

          <Divider orientation="left">测试输入</Divider>
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Form.Item
              label="备注文本（最大300字符）"
              help={
                <Text type="secondary">
                  {text.length}/300 字符
                  {!isPass && errorWords.length > 0 && (
                    <span style={{ color: 'red', marginLeft: 8 }}>
                      (检测到 {errorWords.length} 个敏感词)
                    </span>
                  )}
                </Text>
              }
            >
              <TextArea
                value={text}
                onChange={handleChange}
                rows={6}
                maxLength={300}
                placeholder="请输入备注文本，系统会实时检测敏感词..."
                status={!isPass && text.length > 0 ? 'error' : undefined}
                showCount
              />
            </Form.Item>

            {!isPass && text.length > 0 && (
              <Alert
                message="检测到敏感词"
                description={
                  <div>
                    {errorMessage ? (
                      <div style={{ marginBottom: 8 }}>{errorMessage}</div>
                    ) : null}
                    <div>
                      <Text strong>敏感词列表：</Text>
                      <div style={{ marginTop: 8 }}>
                        {errorWords.map((word, index) => (
                          <Tag key={index} color="red" style={{ marginBottom: 4 }}>
                            {word}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  </div>
                }
                type="error"
                showIcon
              />
            )}

            {isPass && text.length > 0 && (
              <Alert
                message="文本验证通过"
                description="未检测到敏感词，可以提交。"
                type="success"
                showIcon
              />
            )}

            <Space>
              <Button
                type="primary"
                onClick={handleManualCheck}
                disabled={!initialized || text.length === 0}
              >
                手动检查
              </Button>
              <Button
                onClick={() => {
                  // 测试一些预设的敏感词
                  const testText = '这是一段包含政治的文本，还有色情内容';
                  const event = {
                    target: { value: testText },
                    currentTarget: { value: testText },
                  } as React.ChangeEvent<HTMLTextAreaElement>;
                  handleChange(event);
                }}
                disabled={!initialized}
              >
                测试示例1（中文敏感词）
              </Button>
              <Button
                onClick={() => {
                  const testText = 'This is a text with politics and violence';
                  const event = {
                    target: { value: testText },
                    currentTarget: { value: testText },
                  } as React.ChangeEvent<HTMLTextAreaElement>;
                  handleChange(event);
                }}
                disabled={!initialized}
              >
                测试示例2（英文敏感词）
              </Button>
            </Space>
          </Space>

          <Divider orientation="left">功能说明</Divider>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Paragraph>
              <Text strong>主要功能：</Text>
            </Paragraph>
            <ul>
              <li>实时检测：输入时自动检测敏感词</li>
              <li>最多提示3个敏感词，避免提示过多</li>
              <li>支持多语言敏感词检测（中文、英文、西班牙文）</li>
              <li>支持多语言错误提示（通过errorMessageTemplate）</li>
              <li>前端限制最大300字符</li>
              <li>提交前自动去除首尾空白</li>
            </ul>

            <Paragraph>
              <Text strong>使用说明：</Text>
            </Paragraph>
            <ol>
              <li>点击"初始化敏感词列表"按钮加载敏感词配置</li>
              <li>在文本框中输入内容，系统会实时检测</li>
              <li>如果检测到敏感词，会显示红色警告和具体敏感词</li>
              <li>可以使用测试按钮快速填入测试文本</li>
            </ol>
          </Space>
        </Card>
      </div>
  );
};

export default SensitiveWordsTestPage;

